import { useId, useMemo, type ReactElement, type ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import { meetsDecimalTarget } from "@playbookai/types";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  DEFAULT_KPI_TILE_RANGE,
  type KpiTileParams,
  type KpiTileLegacyData,
} from "@playbookai/widget-schemas";
import { KPIValue } from "../../atoms/KPIValue/index.js";
import { cn } from "../../lib/utils.js";
import { useReducedMotion } from "../../hooks/useReducedMotion.js";
import {
  ChartTooltip,
  type ChartTooltipProps,
} from "../../molecules/ChartTooltip/index.js";
import {
  useKpiSourceResolver,
  type KpiSourceResolver,
  type KpiSourceView,
} from "../kpi-source.js";
import type { WidgetBodyProps } from "../types.js";

/**
 * KPI Tile — Specify, multi (addable). A READ-ONLY view of one KPI Manager KPI.
 *
 * Everything on the face of this card comes from KPI Manager through the
 * host-provided `useKpiSource()` seam: the name, the unit, the current value,
 * the applicable target and the sparkline, which is DERIVED from the KPI's own
 * observation ledger rather than a series the tile keeps.
 *
 * The states below are all first-class, because the alternative to saying
 * "no measurement yet" is drawing a zero, and a fabricated zero on a KPI tile is
 * a wrong number somebody will act on.
 */

interface ToneTokens {
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
  stroke: string;
  label: string;
}

const TONE_GOOD: ToneTokens = {
  badgeBg: "bg-pb-green/15",
  badgeText: "text-pb-green-text",
  badgeBorder: "border-pb-green/40",
  stroke: "var(--color-pb-green)",
  label: "On / above target",
};
const TONE_WARN: ToneTokens = {
  badgeBg: "bg-pb-amber-soft",
  badgeText: "text-pb-amber-text",
  badgeBorder: "border-pb-amber/40",
  stroke: "hsl(var(--pb-amber))",
  label: "Off target",
};
const TONE_RANGE_GOOD: ToneTokens = { ...TONE_GOOD, label: "On target" };
const TONE_NEUTRAL: ToneTokens = {
  badgeBg: "bg-pb-card-elevated",
  badgeText: "text-pb-text-muted",
  badgeBorder: "border-pb-border",
  stroke: "hsl(var(--pb-cyan))",
  label: "No target",
};

function toneFor(kpi: KpiSourceView): ToneTokens {
  if (
    kpi.target == null ||
    kpi.currentValue == null ||
    (kpi.higherIsBetter == null && !kpi.targetRange)
  )
    return TONE_NEUTRAL;
  const ok = meetsDecimalTarget(
    kpi.currentValueExact ?? String(kpi.currentValue),
    kpi.targetExact ?? String(kpi.target),
    kpi.higherIsBetter === false,
    kpi.targetRange,
  );
  if (ok === null) return TONE_NEUTRAL;
  return ok ? (kpi.targetRange ? TONE_RANGE_GOOD : TONE_GOOD) : TONE_WARN;
}

/**
 * Why there is no number, in the platform's own terms.
 *
 * KPI Manager suppresses the projected value unless the measurement is
 * `current`, so "no value" is FOUR different facts. Collapsing them into "no
 * measurements yet" would tell a reader nobody has measured when in truth the
 * source went stale — and that is a claim about their team, not about the data.
 */
function suppressedReason(kpi: KpiSourceView): string {
  switch (kpi.measurementStatus) {
    case "not_connected":
      return "No data source connected yet.";
    case "stale":
      return `Source data is stale${
        kpi.observedAt ? ` — last reading ${isoDay(kpi.observedAt)}` : ""
      }.`;
    case "unavailable":
      return "Source data is unavailable.";
    default:
      return "No measurements recorded yet.";
  }
}

/** Locale-independent, like every other date this product renders under test. */
function isoDay(instant: string): string {
  const parsed = new Date(instant);
  return Number.isNaN(parsed.getTime())
    ? instant
    : parsed.toISOString().slice(0, 10);
}

/** The sparkline's hover readout: the reading's day, then its value with the
 *  unit (look row OK-41). recharts clones it with `active`, `label` and
 *  `payload`. */
function ReadingTooltip({
  active,
  label,
  payload,
  unit,
}: Pick<ChartTooltipProps, "active" | "label" | "payload"> & {
  readonly unit: string;
}): ReactElement | null {
  return (
    <ChartTooltip
      active={active}
      label={label === undefined ? undefined : isoDay(String(label))}
      payload={payload}
      formatValue={(value) => `${String(value)}${unit ? ` ${unit}` : ""}`}
    />
  );
}

function Shell({ children }: { children: ReactNode }): ReactElement {
  return <div className="flex-1 min-h-0 flex flex-col gap-2">{children}</div>;
}

function Notice({
  heading,
  detail,
  tone = "muted",
  children,
}: {
  heading: string;
  detail?: string | undefined;
  tone?: "muted" | "warn";
  children?: ReactNode;
}): ReactElement {
  return (
    <Shell>
      <div className="flex-1 min-h-0 grid place-items-center px-3 text-center">
        <div className="space-y-1">
          <p
            className={cn(
              "text-body-small font-medium",
              tone === "warn" ? "text-pb-amber-text" : "text-pb-text",
            )}
          >
            {heading}
          </p>
          {detail ? (
            <p className="text-dense-1 text-pb-text-muted">{detail}</p>
          ) : null}
          {children}
        </div>
      </div>
    </Shell>
  );
}

function OpenInKpiManager({
  href,
  onNavigate,
}: {
  href: string;
  onNavigate?: ((href: string) => void) | undefined;
}): ReactElement {
  return (
    // eslint-disable-next-line ds/no-hand-built -- kept: an 11px muted link with its icon and no underline; Link default and muted are underlined, and no approved look row lists the change.
    <a
      href={href}
      onClick={(e) => {
        // Breadcrumb's rule: only a plain primary click is the host's; the
        // modifier chords and middle-click still open a new tab or window.
        if (!onNavigate || e.defaultPrevented || e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onNavigate(href);
      }}
      className="inline-flex items-center gap-1 text-dense-1 text-pb-text-muted hover:text-pb-text focus-ring rounded-sm"
    >
      <ExternalLink aria-hidden className="h-3 w-3" />
      Open in KPI Manager
    </a>
  );
}

/**
 * The preserved pre-binding card.
 *
 * It is READ-ONLY, and that is the transitional edit policy rather than an
 * omission: the values are a record of what the tile used to say, so letting
 * somebody keep editing them would re-create the independent copy the binding
 * exists to remove. The way forward is to choose a KPI, which is offered in the
 * expanded editor and retires the snapshot.
 */
function PreservedValues({
  legacy,
}: {
  legacy: KpiTileLegacyData;
}): ReactElement {
  return (
    <Shell>
      {/* The 36px hero figure is KPIValue lg (look row SN-16). */}
      <KPIValue
        size="lg"
        tone="neutral"
        value={legacy.value.trim() || "—"}
        unit={legacy.unit?.trim() ? legacy.unit : undefined}
        className="shrink-0"
      />
      <div className="flex-1 min-h-0 grid place-items-center px-3 text-center">
        <div className="space-y-1">
          <p className="text-body-small font-medium text-pb-text">
            Not linked to a KPI yet
          </p>
          <p className="text-dense-1 text-pb-text-muted">
            These values were typed into the tile before KPI Manager became the
            source. They are kept for reference and are no longer editable —
            choose a KPI to replace them.
          </p>
        </div>
      </div>
    </Shell>
  );
}

function LinkedBody({
  kpiId,
  range,
  caption,
  showDetail,
  resolver,
}: {
  kpiId: string;
  range: KpiTileParams["range"];
  caption: string;
  showDetail: boolean;
  resolver: KpiSourceResolver;
}): ReactElement {
  const reducedMotion = useReducedMotion();
  // Stable, always-valid SVG id — a KPI name may contain spaces or leading
  // digits, which are illegal in ids and break the url(#…) reference.
  const gradientId = useId();
  const result = resolver.useKpiSource(kpiId, range ?? DEFAULT_KPI_TILE_RANGE);
  const kpi = result.kpi;
  const sparkData = useMemo(
    () =>
      (kpi?.readings ?? []).map((reading) => ({
        t: reading.observedAt,
        v: reading.value,
      })),
    [kpi?.readings],
  );

  if (result.status === "loading") {
    return <Notice heading="Loading KPI…" />;
  }
  if (result.status === "forbidden") {
    return (
      <Notice
        heading="You don't have access to this KPI"
        detail={
          result.message ??
          "Ask a KPI owner or a Transformation Lead for access, or remove the tile."
        }
      />
    );
  }
  if (result.status !== "ready" || kpi === null) {
    /* The host's own sentence, when it gives one, IS the heading: it knows
       why — KPI Manager turned off for the workspace (ADR-0100, d3) is not a
       KPI that is gone — and the tile must not claim otherwise above it. */
    return (
      <Notice
        heading={result.message ?? "This KPI is no longer available"}
        detail={
          result.message === undefined
            ? "It may have been deleted or moved."
            : undefined
        }
      >
        {result.onRetry ? (
          // eslint-disable-next-line ds/no-hand-built -- kept: an 11px underlined retry in muted ink; Button link neutral inline is full ink, and no approved look row lists the change.
          <button
            type="button"
            onClick={result.onRetry}
            className="text-dense-1 underline text-pb-text-muted hover:text-pb-text"
          >
            Try again
          </button>
        ) : null}
      </Notice>
    );
  }

  const tone = toneFor(kpi);
  const unit = kpi.unit.trim();

  return (
    <Shell>
      {/* The 36px hero figure is KPIValue lg (look row SN-16). */}
      <KPIValue
        size="lg"
        value={kpi.currentValue == null ? "—" : String(kpi.currentValue)}
        unit={unit || undefined}
        deltaSlot={
          kpi.target != null && kpi.currentValue != null ? (
            <span
              className={cn(
                "text-dense-1 uppercase tracking-wider font-semibold py-0.5 px-1.5 rounded-md border",
                showDetail ? "ms-0" : "ms-auto",
                tone.badgeBg,
                tone.badgeText,
                tone.badgeBorder,
              )}
            >
              {tone.label}
            </span>
          ) : null
        }
        className="shrink-0"
      />
      <div className="shrink-0 text-dense-1 text-pb-text-muted space-y-0.5">
        <div>
          {caption.trim() || kpi.name}
          {kpi.target != null
            ? ` — target ${kpi.target}${unit ? ` ${unit}` : ""}`
            : ""}
        </div>
        {kpi.currentValue == null ? (
          <div
            className={
              kpi.measurementStatus === "stale" ||
              kpi.measurementStatus === "unavailable"
                ? "text-pb-amber-text"
                : "text-pb-text"
            }
          >
            {suppressedReason(kpi)}
          </div>
        ) : (
          <div>
            Measured{" "}
            {kpi.observedAt ? isoDay(kpi.observedAt) : "at an unknown date"}
            {kpi.periodLabel ? ` (${kpi.periodLabel})` : ""}
          </div>
        )}
        {kpi.archived ? (
          <div className="text-pb-amber-text">
            This KPI is archived in KPI Manager.
          </div>
        ) : null}
      </div>
      <div className="flex-1 min-h-0">
        {sparkData.length >= 2 ? (
          // Kept hand-built: recharts chart marks, which no design-system part draws.
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={sparkData}
              margin={{ top: 6, right: 4, bottom: 0, left: 4 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={tone.stroke}
                    stopOpacity={0.35}
                  />
                  <stop
                    offset="100%"
                    stopColor={tone.stroke}
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>
              <Tooltip
                cursor={{ stroke: "hsl(var(--pb-border))", strokeWidth: 1 }}
                content={<ReadingTooltip unit={unit} />}
              />
              <Area
                type="monotone"
                dataKey="v"
                name="Value"
                stroke={tone.stroke}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                dot={false}
                isAnimationActive={!reducedMotion}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full grid place-items-center text-dense-1 text-pb-text-muted italic px-3 text-center">
            {sparkData.length === 0
              ? "No readings in this period."
              : "One reading so far — a trend needs two."}
          </div>
        )}
      </div>
      <div className="shrink-0">
        <OpenInKpiManager href={kpi.href} onNavigate={kpi.onNavigate} />
      </div>
    </Shell>
  );
}

export function KpiTileWidget({
  data,
  showDetail = false,
}: WidgetBodyProps<KpiTileParams> & {
  readonly showDetail?: boolean;
}): ReactElement {
  const resolver = useKpiSourceResolver();

  if (data.kpiId !== undefined && data.kpiId !== "") {
    // Each branch is its own component, so the resolver hook is never called
    // conditionally — an unlinked tile mounts a different subtree instead.
    if (resolver === null) {
      return (
        <Notice
          heading="KPI data isn't available here"
          detail="This surface has no KPI Manager connection, so the tile can't show a value."
        />
      );
    }
    return (
      <LinkedBody
        kpiId={data.kpiId}
        range={data.range}
        caption={data.label}
        showDetail={showDetail}
        resolver={resolver}
      />
    );
  }

  if (data.legacy) return <PreservedValues legacy={data.legacy} />;

  return (
    <Notice
      heading="Choose a KPI"
      detail="Open this tile and pick a KPI from KPI Manager. Its value, target and history are shown here; nothing is edited from the tile."
    />
  );
}
